import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Tooltip} from "primereact/tooltip";
import {useEffect, useState} from "react";
import {useSelector} from "react-redux";

import {selectByLanguage} from "@reva/shared";
import {SEGMENT} from "@reva/shared/reva-spec";
import DOMPurify from "dompurify";

import {useRevaTranslation} from "../../hooks/languageHooks";
import nhkGebaeudetypImage from "../../images/nhk_gebaeudetyp.png";
import {selectSegment} from "../../redux/objectParametersSlice";
import {selectSingleApiParamOutputDefCurried} from "../../redux/specSelectors";
import {NHK_2010_COSTS} from "../../util/nhk2010";
import NhkHouseSchema, {isNhkHouseCode} from "./NhkHouseSchema";

/**
 * #17760 Tooltips an Labels.
 */
const LabelWithTooltip = ({
    id,
    label,
    parameterData,
    required,
    hasError,
    className = "",
    hasAssociatedOutputParam,
    extraTooltipText,
    value,
}) => {
    const {t, language} = useRevaTranslation();
    const segment = useSelector(selectSegment);
    const placement = useAdaptiveTooltipPlacement();
    const description = selectByLanguage(language, parameterData?.descriptionDe, parameterData?.descriptionEn);
    const helpText = selectByLanguage(
        language,
        parameterData?.guiAdditionalHelpTextDe,
        parameterData?.guiAdditionalHelpTextEn,
    );

    if (parameterData?.parameter === "building_category_nhk" && segment === SEGMENT.EZFH_K) {
        return (
            <NhkGebaeudetypTooltip
                id={id}
                label={label}
                required={required}
                hasError={hasError}
                className={className}
                description={description}
                value={value}
            />
        );
    }

    const descriptionParts = [description, extraTooltipText].filter(Boolean);
    const associatedOutputParamHint = hasAssociatedOutputParam && t("object.associatedOutputParamHint");

    const tooltip =
        parameterData?.type === "category" ? (
            <CategoryTooltip parameterData={parameterData} helpText={helpText} />
        ) : helpText ? (
            <>
                {descriptionParts.length > 0 && <p className="mt-0">{descriptionParts.join("\n\n")}</p>}
                <HelpText html={helpText} />
                {associatedOutputParamHint && <p className="mb-0">{associatedOutputParamHint}</p>}
            </>
        ) : (
            [...descriptionParts, associatedOutputParamHint].filter(Boolean).join("\n\n")
        );

    return (
        <>
            <label
                id={`tooltip-${id}`}
                htmlFor={id}
                data-pr-at="right bottom"
                {...(helpText && placement.labelProps)}
                className={`${required ? "font-bold" : ""} ${hasError ? "p-error" : ""} ${className}`.trim()}
            >
                {label + (required ? " *" : "")}
            </label>
            {tooltip && (
                <Tooltip
                    target={`#tooltip-${id}`}
                    position="right"
                    className={`custom-tooltip ${helpText ? "adaptive-tooltip" : ""}`.trim()}
                    {...(helpText && placement.tooltipProps)}
                >
                    {tooltip}
                </Tooltip>
            )}
        </>
    );
};

const HelpText = ({html, className = ""}) => (
    <div
        className={`tooltip-help-text ${className}`.trim()}
        // biome-ignore lint/security/noDangerouslySetInnerHtml: Hilfetext aus der AVM-Spec, per DOMPurify bereinigt
        dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(html)}}
    />
);

export const CategoryTooltip = ({parameterData, helpText}) => {
    const {t, language} = useRevaTranslation();
    return (
        <>
            <span>{selectByLanguage(language, parameterData?.descriptionDe, parameterData.descriptionEn)}</span>
            {helpText && <HelpText html={helpText} className="mt-4" />}
            <p>{t("object.categoryTooltip")}</p>
            <DataTable value={parameterData?.categories} size={"small"} unstyled className={"text-left"}>
                <Column
                    field={selectByLanguage(language, "shortNameDe", "shortNameEn")}
                    header="Name"
                    className={"pr-6 whitespace-nowrap"}
                />
                <Column
                    field={selectByLanguage(language, "titleDe", "titleEn")}
                    header={t("market.category.description")}
                />
            </DataTable>
        </>
    );
};

// #18008 Für hohe Tooltips (NHK-Bild, Hilfetext-Tabellen #19411): Das Label kann sowohl nahe am unteren Rand
// (Objekt-Überblick) als auch nahe am oberen Rand (Besichtigung-Tab) stehen, daher wird die
// Ausrichtung beim Öffnen dynamisch anhand des verfügbaren Platzes gewählt (statt fix). Passt
// der Inhalt in keiner Richtung ganz, wird er auf den verfügbaren Platz begrenzt und
// scrollbar (max-height/overflow), statt über den Bildschirmrand hinauszuragen; autoHide=false,
// damit der Tooltip beim Scrollen offen bleibt, hideDelay für die Lücke zwischen Label und Tooltip.
const useAdaptiveTooltipPlacement = () => {
    const [growUp, setGrowUp] = useState(true);
    const [maxTooltipHeight, setMaxTooltipHeight] = useState(null);
    return {
        labelProps: {
            "data-pr-at": `right ${growUp ? "bottom" : "top"}`,
            "data-pr-my": `left ${growUp ? "bottom" : "top"}`,
        },
        tooltipProps: {
            autoHide: false,
            hideDelay: 150,
            onBeforeShow: (e) => {
                const rect = e.target.getBoundingClientRect();
                const spaceAbove = rect.top;
                const spaceBelow = window.innerHeight - rect.bottom;
                setGrowUp(spaceAbove > spaceBelow);
                setMaxTooltipHeight(Math.max(spaceAbove, spaceBelow) - 24);
            },
            pt: {
                arrow: {
                    style: growUp
                        ? {top: "auto", bottom: "1px", marginTop: 0}
                        : {bottom: "auto", top: "1px", marginTop: 0},
                },
                text: {style: {maxHeight: maxTooltipHeight, overflowY: "auto"}},
            },
        },
    };
};

const STANDARD_LEVELS = [1, 2, 3, 4, 5];

// #18008 NHK-Gebäudetyp: Tooltip mit Bild.
export const NhkGebaeudetypTooltip = ({id, label, required, hasError, className, description, value}) => {
    const {t, language} = useRevaTranslation();
    const placement = useAdaptiveTooltipPlacement();
    const costs = isNhkHouseCode(value) ? NHK_2010_COSTS[value] : undefined;
    const category = useSelector(selectSingleApiParamOutputDefCurried("building_category_nhk_cat"))?.categories?.find(
        ({shortNameDe}) => shortNameDe === value,
    );

    // Bild schon beim Rendern laden statt erst beim Hover: PrimeReact misst die Tooltip-Position
    // synchron beim Öffnen — ist das Bild dann noch nicht geladen/gelayoutet, positioniert es
    // sich falsch (reine showDelay-Verzögerung ist dafür nicht zuverlässig genug).
    useEffect(() => {
        const preload = new Image();
        preload.src = nhkGebaeudetypImage;
    }, []);

    return (
        <>
            <label
                id={`tooltip-${id}`}
                htmlFor={id}
                {...placement.labelProps}
                className={`${required ? "font-bold" : ""} ${hasError ? "p-error" : ""} ${className}`.trim()}
            >
                {label + (required ? " *" : "")}
            </label>
            {description && (
                <Tooltip
                    target={`#tooltip-${id}`}
                    position="right"
                    showDelay={300}
                    className={`custom-tooltip nhk-tooltip ${costs ? "nhk-tooltip-single" : ""}`.trim()}
                    {...placement.tooltipProps}
                >
                    {description}
                    <br />
                    <br />
                    {costs ? (
                        <>
                            <div className="flex items-center gap-4">
                                <NhkHouseSchema code={value} className="shrink-0 bg-white rounded" />
                                <div>
                                    <div className="font-bold mb-2">{value}</div>
                                    {category &&
                                        selectByLanguage(language, category.titleDe, category.titleEn)
                                            .split("; ")
                                            .map((part) => <div key={part}>{part}</div>)}
                                </div>
                            </div>
                            <div className="mt-3 whitespace-normal">
                                <div className="mb-1">{t("object.nhkCosts.unit")}</div>
                                <DataTable value={[{unit: "€/m²", costs}]} size="small" unstyled className="nhk-costs">
                                    <Column field="unit" header={t("object.nhkCosts.standardLevel")} />
                                    {STANDARD_LEVELS.map((level) => (
                                        <Column
                                            key={level}
                                            header={level}
                                            body={(row) => row.costs[level - 1].toLocaleString(language)}
                                        />
                                    ))}
                                </DataTable>
                                <div className="mt-1 text-xs">
                                    {t("object.nhkCosts.footnote")}
                                    {value.startsWith("1.") && <div>{t("object.nhkCosts.footnoteTwoFamily")}</div>}
                                </div>
                            </div>
                        </>
                    ) : (
                        <img src={nhkGebaeudetypImage} width={1453} height={993} className="w-full h-auto" alt="" />
                    )}
                </Tooltip>
            )}
        </>
    );
};

export default LabelWithTooltip;
